// @flow
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Banner,
  Stepper,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import useReactHookFormProps from '../../../../../hooks/useReactHookFormProps';
import AffordableService from '../../../../../services/affordableService';

const AFFORDABLE_PROGRAM_OPTIONS = [
  'HUD',
  'LIHTC',
  'HOME',
  'RD',
  'Deferred Loan',
  'NOAH',
  'BOND',
  'PH',
].map((value) => ({ value, text: value }));
const PROGRAM_OPTIONS = [
  '811 PRA Demo',
  'HOME',
  'LIHTC',
  'Project Based Section 8',
  'Rural Development',
  'NOAH',
  'Deferred Loan',
  'BOND',
  'HTF',
  'TCAP',
  'KCMO',
  'PH',
].map((value) => ({ value, text: value }));

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];
const HUD_TYPE_OPTIONS = ['Low', 'Very Low', 'Extremely Low'].map((value) => ({
  value,
  text: value,
}));
const RD_INCOME_TYPE_OPTIONS = [
  'Moderate',
  'Low',
  'Very Low',
  'Extremely Low',
].map((value) => ({ value, text: value }));
const PERSON_FIELDS = [
  ['onePerson', '1 Person Amount', '18500'],
  ['twoPeople', '2 Person Amount', '21150'],
  ['threePeople', '3 Person Amount', '23800'],
  ['fourPeople', '4 Person Amount', '26400'],
  ['fivePeople', '5 Person Amount', '28550'],
  ['sixPeople', '6 Person Amount', '30650'],
  ['sevenPeople', '7 Person Amount', '32750'],
  ['eightPeople', '8 Person Amount', '34850'],
  ['ninePeople', '9 Person Amount', '36950'],
  ['tenPeople', '10 Person Amount', '39050'],
  ['elevenPeople', '11 Person Amount', '41150'],
  ['twelvePeople', '12 Person Amount', '43250'],
];
const REQUIRED_PERSON_FIELD_COUNT = 8;
const MINIMUM_AMOUNT = 1;
const MAXIMUM_AMOUNT = 1000000;
// Existing properties legitimately carry set asides with no units allocated yet.
const MINIMUM_NUMBER_OF_UNITS = 0;
const DEFAULT_VALUES = {
  affordableProgramName: '',
  programName: '',
  setAsideName: '',
  setAsideShortName: '',
  isHUD: '',
  hudType: '',
  allocationDate: '',
  effectiveDate: '',
  numberOfUnits: '',
  incomeLimit: '',
  incomeLimitType: '',
  maximumIncomePercentage: '',
  maximumRentPercentage: '',
  overIncomeCalc: '',
  ...PERSON_FIELDS.reduce((values, [name]) => ({ ...values, [name]: '' }), {}),
};
const DUPLICATE_ERROR_MESSAGE =
  'A set aside limit with this Affordable Program Name, Set-Aside Program Name, and ' +
  'Set-Aside Name already exists at this property. If you need to update this set aside limit, ' +
  'edit the existing record. To continue creating a new set aside limit, change one or more ' +
  'of these values.';

const normalize = (value) =>
  String(value || '')
    .trim()
    .toLocaleLowerCase();
const getErrorMessage = (error) =>
  error?.networkError ||
  error?.response?.data?.message ||
  error?.message ||
  String(error);
const isDuplicateError = (error) =>
  error?.statusCode === 409 ||
  error?.response?.status === 409 ||
  error?.response?.data?.statusCode === 409 ||
  getErrorMessage(error).includes('already exists at this property');

export const flattenSetAsidePrograms = (groups: Array<Object>): Array<Object> =>
  (groups || []).reduce(
    (setAsides, group) =>
      setAsides.concat(
        group.fullSetAsides ||
          (group.setAsideProgram ? [group.setAsideProgram] : [group]),
      ),
    [],
  );

export const isDuplicateSetAside = (
  groups: Array<Object>,
  values: Object,
): boolean =>
  flattenSetAsidePrograms(groups).some(
    (setAside) =>
      normalize(setAside.affordableProgramName) ===
        normalize(values.affordableProgramName) &&
      normalize(setAside.programName) === normalize(values.programName) &&
      normalize(setAside.setAsideName) === normalize(values.setAsideName),
  );

const requiredField = (name, label, extra = {}) => ({
  variant: 'text',
  name,
  label,
  helperText: 'Required',
  rules: { required: true },
  GridProps: { xs: 12, sm: 6 },
  ...extra,
});
const exampleOf = (example) => `e.g. ${example}`;
// Percentages are stored with a trailing "%" but every other screen strips it
// back off to read the number, so the wizard collects digits and adds the
// suffix on save the same way the edit modal does.
const PERCENTAGE_EXAMPLE = '40';
const PERCENTAGE_PATTERN = {
  value: /^\d+(?:\.\d+)?%?$/,
  message: `Enter a percentage, for example ${PERCENTAGE_EXAMPLE}.`,
};
const percentageField = (name, label, { isRequired = true } = {}) =>
  requiredField(name, label, {
    placeholder: exampleOf(PERCENTAGE_EXAMPLE),
    helperText: isRequired ? 'Required' : 'Optional',
    rules: { required: isRequired, pattern: PERCENTAGE_PATTERN },
  });
// Accepts "40" or "40%" from the user and always stores "40%".
const withPercentSign = (value) => {
  const digits = String(value ?? '').trim();
  if (digits === '') return null;
  return digits.endsWith('%') ? digits : `${digits}%`;
};

const buildPayload = (values) => {
  const isHUD = values.affordableProgramName === 'HUD';
  const isHUDSetAsideProgram = isHUD && values.isHUD === 'true';
  const isRD = values.affordableProgramName === 'RD';
  return {
    ...values,
    isHUD: isHUD ? values.isHUD === 'true' : null,
    hudType: isHUDSetAsideProgram ? values.hudType : null,
    incomeLimitType: isRD ? values.incomeLimitType : null,
    allocationDate: moment(values.allocationDate).format('YYYY-MM-DD'),
    effectiveDate: moment(values.effectiveDate).format('YYYY-MM-DD'),
    maximumIncomePercentage: withPercentSign(values.maximumIncomePercentage),
    maximumRentPercentage: withPercentSign(values.maximumRentPercentage),
    overIncomeCalc: withPercentSign(values.overIncomeCalc),
    numberOfUnits: values.numberOfUnits === '' ? null : values.numberOfUnits,
    ...PERSON_FIELDS.reduce(
      (amounts, [name]) => ({
        ...amounts,
        [name]: values[name] === '' ? null : values[name],
      }),
      {},
    ),
  };
};

type Props = {
  open: boolean,
  onClose: () => void,
  organizationId: string,
  propertyId: string,
  refresh: () => void,
  promptToaster: (Object) => void,
};

const AddSetAsideWizard = ({
  open,
  onClose,
  organizationId,
  propertyId,
  refresh,
  promptToaster,
}: Props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [duplicateError, setDuplicateError] = useState(false);
  const sessionVersion = useRef(0);
  const form = useReactHookFormProps({
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
  });
  const { reset } = form;
  const affordableProgramName = form.watch('affordableProgramName');
  const isHUDSetAsideAnswer = form.watch('isHUD');
  const isHUDProgram = affordableProgramName === 'HUD';
  const isRDProgram = affordableProgramName === 'RD';
  const isHUDSetAside = isHUDSetAsideAnswer === 'true';

  useEffect(() => {
    sessionVersion.current += 1;
    if (open) {
      setActiveStep(0);
      reset(DEFAULT_VALUES);
      setDuplicateError(false);
      setIsCheckingDuplicate(false);
      setIsSaving(false);
    }
  }, [open, organizationId, propertyId, reset]);

  const { setValue, clearErrors } = form;
  useEffect(() => {
    if (isHUDProgram) return;
    setValue('isHUD', '');
    setValue('hudType', '');
    clearErrors(['isHUD', 'hudType']);
  }, [isHUDProgram, setValue, clearErrors]);

  useEffect(() => {
    if (isHUDProgram && isHUDSetAside) return;
    setValue('hudType', '');
    clearErrors('hudType');
  }, [isHUDProgram, isHUDSetAside, setValue, clearErrors]);

  useEffect(() => {
    if (isRDProgram) return;
    setValue('incomeLimitType', '');
    clearErrors('incomeLimitType');
  }, [isRDProgram, setValue, clearErrors]);

  const firstStepFields = [
    requiredField('affordableProgramName', 'Affordable Program Name', {
      variant: 'singleSelect',
      options: AFFORDABLE_PROGRAM_OPTIONS,
    }),
    requiredField('programName', 'Set-Aside Program Name', {
      variant: 'singleSelect',
      options: PROGRAM_OPTIONS,
    }),
    requiredField('setAsideName', 'Set-Aside Name', {
      placeholder: exampleOf('42 units at or below 40% of median income'),
    }),
    requiredField('setAsideShortName', 'Set-Aside Short Name', {
      placeholder: exampleOf('MTSP - 40%'),
    }),
    ...(isHUDProgram
      ? [
          requiredField('isHUD', 'Is this a HUD Set-Aside Program?', {
            variant: 'radioGroup',
            options: YES_NO_OPTIONS,
            sx: { flexDirection: 'row' },
          }),
        ]
      : []),
    ...(isHUDProgram && isHUDSetAside
      ? [
          requiredField('hudType', 'HUD Type', {
            variant: 'singleSelect',
            options: HUD_TYPE_OPTIONS,
          }),
        ]
      : []),
    requiredField('allocationDate', 'Allocation Date', { variant: 'date' }),
    requiredField('effectiveDate', 'Effective Date', { variant: 'date' }),
    {
      variant: 'number',
      name: 'numberOfUnits',
      label: 'Number of Units',
      placeholder: exampleOf('42'),
      rules: { min: MINIMUM_NUMBER_OF_UNITS },
      GridProps: { xs: 12, sm: 6 },
    },
  ];

  const secondStepFields = [
    requiredField('incomeLimit', 'Income Limit', {
      placeholder: exampleOf('40%'),
    }),
    ...(isRDProgram
      ? [
          requiredField('incomeLimitType', 'Income Type Limit', {
            variant: 'singleSelect',
            options: RD_INCOME_TYPE_OPTIONS,
          }),
        ]
      : []),
    percentageField('maximumIncomePercentage', 'Percent of Median Income'),
    percentageField('maximumRentPercentage', 'Maximum Rent % of Median Income'),
    percentageField('overIncomeCalc', 'Over Income at Recert Calculations', {
      isRequired: false,
    }),
    {
      variant: 'custom',
      name: 'peopleAmountsHeader',
      component: (
        <Typography variant="bodyMedium">
          Enter the income level variance amount based on the amount of people
          in a household.
        </Typography>
      ),
      GridProps: { xs: 12 },
    },
    ...PERSON_FIELDS.map(([name, label, example], index) => ({
      variant: 'number',
      name,
      label,
      adornment: '$',
      placeholder: exampleOf(example),
      helperText: index < REQUIRED_PERSON_FIELD_COUNT ? 'Required' : 'Optional',
      rules: {
        required: index < REQUIRED_PERSON_FIELD_COUNT,
        min: MINIMUM_AMOUNT,
        max: MAXIMUM_AMOUNT,
      },
      GridProps: { xs: 12, sm: 6 },
    })),
  ];
  const fields = [firstStepFields, secondStepFields][activeStep];

  const close = () => {
    form.reset(DEFAULT_VALUES);
    setActiveStep(0);
    setDuplicateError(false);
    onClose();
  };

  const continueToNextStep = async () => {
    const valid = await form.trigger(
      firstStepFields.map(({ name }) => name).filter(Boolean),
    );
    if (!valid) return;
    const requestSession = sessionVersion.current;

    setIsCheckingDuplicate(true);
    try {
      const affordableService = new AffordableService();
      const currentSetAsides =
        await affordableService.getAllSetAsideProgramsForProperty(
          organizationId,
          propertyId,
        );
      if (requestSession !== sessionVersion.current) return;
      const duplicate = isDuplicateSetAside(currentSetAsides, form.getValues());
      setDuplicateError(duplicate);
      if (!duplicate) setActiveStep(1);
    } catch (error) {
      if (requestSession !== sessionVersion.current) return;
      promptToaster({
        type: 'error',
        title: 'Unable to Validate Set-Aside',
        message: getErrorMessage(error),
      });
    } finally {
      if (requestSession === sessionVersion.current)
        setIsCheckingDuplicate(false);
    }
  };

  const save = async (values) => {
    setIsSaving(true);
    const requestSession = sessionVersion.current;
    try {
      const affordableService = new AffordableService();
      await affordableService.createSetAsideProgram(
        organizationId,
        propertyId,
        buildPayload(values),
      );
      if (requestSession !== sessionVersion.current) return;
      promptToaster({
        type: 'success',
        title: 'Set-Aside Added',
        message: 'The new set-aside limit was saved.',
      });
      refresh();
      close();
    } catch (error) {
      if (requestSession !== sessionVersion.current) return;
      if (isDuplicateError(error)) {
        setActiveStep(0);
        setDuplicateError(true);
        return;
      }
      promptToaster({
        type: 'error',
        title: 'Unable to Add Set-Aside',
        message: getErrorMessage(error),
      });
    } finally {
      if (requestSession === sessionVersion.current) setIsSaving(false);
    }
  };

  const isBusy = isCheckingDuplicate || isSaving;
  const requestClose = () => {
    if (!isBusy) close();
  };

  const actionsProps = [
    {
      children: 'Cancel',
      variant: 'text',
      onClick: requestClose,
      disabled: isBusy,
    },
    ...(activeStep > 0
      ? [
          {
            children: 'Back',
            variant: 'text',
            onClick: () => setActiveStep(activeStep - 1),
            disabled: isBusy,
          },
        ]
      : []),
    activeStep === 0
      ? {
          children: 'Next',
          onClick: continueToNextStep,
          disabled: isCheckingDuplicate,
          isSubmitting: isCheckingDuplicate,
        }
      : {
          children: 'Save',
          onClick: form.handleSubmit(save),
          disabled: isSaving,
          isSubmitting: isSaving,
        },
  ];

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title="Add New Set-Aside Limit"
      actionsProps={actionsProps}
      ModalProps={{
        sx: { '.MuiPaper-root': { maxWidth: '760px', width: '100%' } },
      }}
    >
      <Stepper
        activeStep={activeStep}
        steps={[
          { label: { text: 'Set-Aside Details' } },
          { label: { text: 'Income Limits' } },
        ]}
      />
      {activeStep === 0 && duplicateError && (
        <Banner color="red" text={DUPLICATE_ERROR_MESSAGE} />
      )}
      <Form
        ReactHookFormProps={form}
        fields={fields}
        spacing={2}
        containerXS={12}
      />
    </Modal>
  );
};

export default AddSetAsideWizard;
